import { Box } from "@ui/box/Box"
import Decimal from "decimal.js"
import { useEffect, useMemo } from "react"
import { toast } from "react-toastify"
import { GD } from "src/GD"
import { Section } from "../ds/Kit"
import { FormParser } from "../FormParser"
import { CardSubView } from "./CardsList"

const MONEY_FIELDS = ["limit_daily", "limit_monthly", "balance_threshold", "balance_topup_target"] as const
const FORM_ACTIONS = {
	position: "static", marginTop: "16px", paddingTop: "0px", paddingBottom: "0px",
	borderTop: "none", boxShadow: "none", backgroundColor: "transparent", backdropFilter: "none"
} as const
const LIMIT_FIELDS = {
	gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "14px 18px", alignItems: "start",
	"& > *": { minWidth: 0 },
	"@media (max-width: 620px)": { gridTemplateColumns: "minmax(0, 1fr)" }
} as const

export const CardEditForms = ({ card, onSaved, onBack }: {
	card: ICardSaved
	onSaved: (changes: Partial<ICardSaved>) => Promise<void>
	onBack?: () => void
}) => {
	const scope = useMemo(() => ({ active: true }), [card.uid])
	useEffect(() => {
		scope.active = true
		return () => { scope.active = false }
	}, [scope])
	return (
		<Box data-dialog-layout='compact-form' sx={{ width: "100%", maxWidth: "632px", margin: "0 auto", minWidth: 0 }}>
			<CardSubView
				title='Edit card'
				description='Rename the card and set its spending limits and automatic top-up.'
				card={card}
				onBack={onBack}
				backTitle='Back to card'
			>
				<Box role='region' aria-label='Card name'>
					<Section title='Card name'>
						<FormParser
							key={`name:${card.uid}`}
							actionsSx={FORM_ACTIONS}
							onRequest={async () => {
								const res = await GD.REQ_CARD_NAME_GET.request({ uid: card.uid })
								return res.error ? res : { ...res.data, $__uid_hidden: true }
							}}
							onSubmit={async (data: ICardEditModel) => {
								const name = String(data.name ?? "").trim()
								const res = await GD.REQ_CARD_NAME_SET.request({ uid: card.uid, name })
								if (!scope.active) return res
								if (res.error || !res.data?.status) return res
								await onSaved({ name })
								toast.success("Card name saved.")
								return { data: { ...data, name } }
							}}
							primaryButton={{ title: "Save name" }}
						/>
					</Section>
				</Box>
				<Box role='region' aria-label='Card limits'>
					<Section title='Limits & automatic top-up'>
						<FormParser
							key={`limits:${card.uid}`}
							fieldsSx={LIMIT_FIELDS}
							actionsSx={FORM_ACTIONS}
							onRequest={async () => {
								const res = await GD.REQ_CARD_LIMITS_GET.request({ uid: card.uid })
								if (res.error) return res
								const model: ICardEditModel = { ...res.data, $__uid_hidden: true }
								for (const field of MONEY_FIELDS) {
									if (!(field in model)) continue
									model[field] = model[field] == null ? "" : new Decimal(String(model[field])).div(100).toNumber()
									model[`$__${field}_type`] = "amount"
									model[`$__${field}_units`] = ` ${card.currency || "USD"}`
									if (field === "limit_daily" || field === "limit_monthly") {
										const baseline = field === "limit_daily" ? 1000 : 5000
										model[`$__${field}_slider`] = {
											min: 0, max: Math.max(baseline, Math.ceil(Number(model[field]) / baseline) * baseline),
											step: 1, zeroLabel: "No limit (0)"
										}
									}
								}
								return model
							}}
							onSubmit={async (data: ICardEditModel) => {
								const amounts: Partial<Record<typeof MONEY_FIELDS[number], number>> = {}
								for (const field of MONEY_FIELDS) {
									const value = String(data[field] ?? "").replace(/\s/g, "").replace(",", ".")
									if (field === "balance_topup_target" && !value) continue
									const label = String(data[`$__${field}_label`] ?? field)
									let message = ""
									try {
										const amount = new Decimal(value)
										if (!amount.isFinite() || amount.isNegative()) message = `${label} must be zero or greater.`
										else if (amount.decimalPlaces() > 2) message = `${label} can have at most two decimal places.`
										else amounts[field] = amount.mul(100).toNumber()
									} catch {
										message = `${label} must be a valid amount.`
									}
									if (message) return { error: { code: 0, message, details: [`"${field}" ${message}`] } }
								}
								if (amounts.limit_daily! > 0 && amounts.limit_monthly! > 0 && amounts.limit_daily! > amounts.limit_monthly!) {
									return { error: { code: 0, message: "Daily spending limit cannot be greater than the monthly limit.", details: ['"limit_daily" must not exceed "limit_monthly"'] } }
								}
								const res = await GD.REQ_CARD_LIMITS_SET.request({
									uid: card.uid,
									limit_daily: amounts.limit_daily!,
									limit_monthly: amounts.limit_monthly!,
									balance_threshold: amounts.balance_threshold!,
									...(amounts.balance_topup_target === undefined ? {} : { balance_topup_target: amounts.balance_topup_target })
								})
								if (!scope.active) return res
								if (res.error || !res.data?.status) return res
								await onSaved(amounts)
								toast.success("Card limits saved.")
								return { data }
							}}
							primaryButton={{ title: "Save limits" }}
						/>
					</Section>
				</Box>
			</CardSubView>
		</Box>
	)
}
